import React from 'react';
import type { ScreenerQuestion } from '../types/screener';
import { isTextInputQuestion } from '../types/screener';

interface ScreenerQuestionItemProps {
  question: ScreenerQuestion;
  answerKey: string;
  selectedAnswer: string | undefined;
  onAnswerChange: (answerKey: string, value: string) => void;
}

const ScreenerQuestionItem: React.FC<ScreenerQuestionItemProps> = ({
  question,
  answerKey,
  selectedAnswer,
  onAnswerChange,
}) => {
  const hasOptions = question.options && question.options.length > 0;
  const hasPossibleAnswers = question.possibleanswers && question.possibleanswers.length > 0;
  const isRequired = question.required !== false;

  if (isTextInputQuestion(question)) {
    return (
      <section className="flex flex-col gap-3">
        <div className="text-base font-semibold text-[#1C1C1C]">
          {question.question} {isRequired && <span className="text-red-500">*</span>}
        </div>
        <input
          type={question.questiontype === 'NUMERIC' ? 'number' : 'text'}
          className="w-full border border-[#DCE1E8] rounded-lg py-3 px-4 text-sm text-[#1C1C1C] focus:outline-none focus:border-[#01545D] focus:ring-1 focus:ring-[#01545D]"
          placeholder={question.questiontype === 'NUMERIC' ? 'Enter a number' : 'Enter your response'}
          value={selectedAnswer || ''}
          onChange={(e) => onAnswerChange(answerKey, e.target.value)}
        />
      </section>
    );
  }

  if (!hasOptions && !hasPossibleAnswers) return null;

  return (
    <section className="flex flex-col gap-3">
      <div className="text-base font-semibold text-[#1C1C1C]">
        {question.question} {isRequired && <span className="text-red-500">*</span>}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {hasOptions
          ? question.options!.map((opt) => (
              <label
                key={opt.optionid}
                className={
                  'flex flex-col items-center justify-center border rounded-lg py-3 px-4 text-center cursor-pointer ' +
                  (selectedAnswer === opt.optiontext
                    ? 'border-[#01545D] bg-[#E0F2F3]'
                    : 'border-[#DCE1E8] hover:border-[#01545D]')
                }
              >
                <input
                  type="radio"
                  name={answerKey}
                  value={opt.optiontext}
                  className="hidden"
                  required={isRequired}
                  checked={selectedAnswer === opt.optiontext}
                  onChange={() => onAnswerChange(answerKey, opt.optiontext)}
                />
                <span className="text-sm text-[#1C1C1C]">{opt.optiontext}</span>
              </label>
            ))
          : question.possibleanswers!.map((opt) => (
              <label
                key={opt}
                className={
                  'flex flex-col items-center justify-center border rounded-lg py-3 px-4 text-center cursor-pointer ' +
                  (selectedAnswer === opt
                    ? 'border-[#01545D] bg-[#E0F2F3]'
                    : 'border-[#DCE1E8] hover:border-[#01545D]')
                }
              >
                <input
                  type="radio"
                  name={answerKey}
                  value={opt}
                  className="hidden"
                  required={isRequired}
                  checked={selectedAnswer === opt}
                  onChange={() => onAnswerChange(answerKey, opt)}
                />
                <span className="text-sm text-[#1C1C1C]">{opt}</span>
              </label>
            ))}
      </div>
    </section>
  );
};

export default ScreenerQuestionItem;
